import React from 'react';
import { SendJsonMessage } from 'react-use-websocket/dist/lib/types';
import { check, clear, incrementErrorCount } from '../../../../../backoff';
import { useOrganisationData } from '../../../../Hooks/useOrganisation';

export interface SpeechRecognitionAlternative {
  confidence: number;
  transcript: string;
}

export interface SpeechRecognitionResult {
  0: SpeechRecognitionAlternative,
  length: number;
  isFinal: boolean;
}

export interface SpeechRecognitionResultList {
  [index: number]: SpeechRecognitionResult;
  length: number;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export default function useSpeechRecognition(
  language: string | undefined | null,
  sendJsonMessage: SendJsonMessage,
  recognitionFactory: () => any,
  sessionId: string | null,
) {
  const inProgress = !!language;
  const speechRecognition = React.useRef<any | null>(null);
  const { enableAlternativeAsrApi: settingsEnabled } = useOrganisationData();
  const transcriptCountRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!inProgress) {
      transcriptCountRef.current = 0;
    }
  }, [inProgress]);

  React.useEffect(() => {
    transcriptCountRef.current = 0;
    if (speechRecognition.current) {
      speechRecognition.current.onerror = undefined;
      speechRecognition.current.onnomatch = undefined;
      speechRecognition.current.onend = undefined;
      speechRecognition.current.onresult = undefined;
      speechRecognition.current.stop?.();
    }
    speechRecognition.current = recognitionFactory();
  }, [sessionId, recognitionFactory]);

  const enabled = React.useMemo(() => inProgress && settingsEnabled, [inProgress, settingsEnabled]);
  const enabledRef = React.useRef(enabled);

  React.useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  function startRecognition() {
    const timeoutMs = check();
    function start() {
      try {
        speechRecognition.current?.start();
      } catch (err: any) {
        if (
          err.message !== "Failed to execute 'start' on 'SpeechRecognition': recognition has already started."
        ) {
          console.error('SpeechRecognition: An error has occurred starting the speech recognition', err);
        }
      }
    }
    if (!timeoutMs) start();
    else setTimeout(() => { if (enabledRef.current) start(); }, timeoutMs);
  }

  const sendTranscript = React.useCallback((transcript: string, isFinal: boolean) => {
    sendJsonMessage({
      text: transcript,
      type: isFinal ? 'transcript' : 'text',
      date: Date.now() * 1e6,
    });
  }, [sendJsonMessage]);

  React.useEffect(() => {
    if (enabled && speechRecognition.current) {
      speechRecognition.current.lang = language;
      speechRecognition.current.onerror = (err) => {
        if (err.error !== 'not-allowed' && err.error !== 'no-speech') {
          console.error('SpeechRecognition: An error has occurred', err.error);
        }
        incrementErrorCount();
      };
      speechRecognition.current.onnomatch = (event) => {
        console.error('SpeechRecognition: Speech not recognized', event.error);
      };

      speechRecognition.current.onend = () => {
        if (enabled) {
          startRecognition();
        }
      };

      startRecognition();
      return () => {
        if (speechRecognition.current) {
          speechRecognition.current.onerror = undefined;
          speechRecognition.current.onnomatch = undefined;
          speechRecognition.current.onend = undefined;
        }
        speechRecognition.current?.stop();
      };
    }
    return undefined;
  }, [language, enabled, sessionId]);

  const handleInterimTranscripts = React.useCallback((results: SpeechRecognitionResult[]) => {
    const newTemporaryTranscript = results.filter((res) => res.isFinal === false).map((res) => res[0].transcript).join('');
    // only send last 10 words
    const trimmedTemporaryTranscript = newTemporaryTranscript.split(' ').slice(-15).join(' ');
    if (newTemporaryTranscript !== '') {
      sendTranscript(trimmedTemporaryTranscript, false);
    }
  }, [sendTranscript]);

  const handleFinalTranscripts = React.useCallback((results: SpeechRecognitionResult[]) => {
    const finalTranscripts = results.filter((res) => res.isFinal === true).map((res) => res[0].transcript);
    if (finalTranscripts.length === 0) {
      transcriptCountRef.current = 0;
    } else if (finalTranscripts.length < transcriptCountRef.current) {
      console.error('The speech recognition results were not reset to zero. This should never happen.');
    }

    const newFinalTranscripts = finalTranscripts.slice(transcriptCountRef.current, finalTranscripts.length);
    newFinalTranscripts.forEach((text) => { sendTranscript(text, true); });
    transcriptCountRef.current = finalTranscripts.length;
  }, [sendTranscript]);

  React.useEffect(() => {
    if (enabled && speechRecognition.current) {
      speechRecognition.current.onresult = (event) => {
        clear();
        const results = event.results as SpeechRecognitionResultList;
        handleInterimTranscripts(Array.from(results));
        handleFinalTranscripts(Array.from(results));
      };
    }
    return undefined;
  }, [enabled, sendTranscript, handleInterimTranscripts, handleFinalTranscripts, speechRecognition.current]);
}
